import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CertificateCorporateListdata } from "./data/CertificateCorporateList.data";
import FormCreator from "../../../components/Forms/FormCreator";
import { decryptAESID, encryptAESID } from "../../../utils/Encryption";
import { useDownloadMUAPDFMutation, useGetMUAPathsByApplicationIdMutation, useDownloadIssuedCertificateMutation, useLazyGetAllSGBPApplicationListByCorporateMemberIdForAdminQuery, useLazyGetAllSGBPCertificateListByCorporateMemberIdForAdminQuery, useLazyGetAllSGBSApplicationListByCorporateMemberIdForAdminQuery, useLazyGetAllSGBSCertificateListByCorporateMemberIdForAdminQuery, useLazyGetCompanyInformationByMemberIdQuery, } from "../../../app/services/CorporateCertificate/corporateCertificateApi";
import moment from "moment";
import { saveAs } from "file-saver";
import DataLoader from "../../../components/ui/dataLoader/DataLoader";
import NoRecordFound from "../../../components/ui/noRecordFound/NoRecordFound";
import { useNavigate } from "react-router-dom";
import ToastService from "../../../services/toastService/ToastService";
import CertificationRenewalModal from "./components/CertificationRenewalModal";
import SwalAlert from "../../../services/swalService/SwalService";
import Buttons from "../../../components/ui/button/Buttons";

const PAGE_SIZE = 5;

const formatDate = (date) => {
  if (!date) return "-";
  const m = moment(date);
  return m.isValid() ? m.format("DD MMM YYYY") : "-";
};

const statusBadgeClass = (status) => `status-badge status-${(status || "").toLowerCase().replace(/\s+/g, "-")}`;

const useLoadMore = (data, pageSize = PAGE_SIZE) => {
  const list = Array.isArray(data) ? data : [];
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => { setIsExpanded(false); }, [data]);
  const canToggle = list.length > pageSize;
  const visibleData = isExpanded ? list : list.slice(0, pageSize);
  const toggle = () => setIsExpanded((prev) => !prev);
  return { visibleData, canToggle, isExpanded, toggle, total: list.length };
};

const LoadMoreButton = ({ canToggle, isExpanded, onClick }) => {
  if (!canToggle) return null;
  return (
    <div className="load-more-container">
      <button type="button" className="btn-load-more" onClick={onClick}>
        {isExpanded ? "Load Less" : "Load More"}
      </button>
    </div>
  );
};

const ViewCertificateCorporateAccount = () => {
  const MemberApplicationRef = useRef();
  const { id } = useParams();
  const editID = decryptAESID(id, false);

  const { error } = SwalAlert();

  const navigate = useNavigate();
  const [formData, setFormData] = useState(CertificateCorporateListdata);
  const [downloadingMUAId, setDownloadingMUAId] = useState(null);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedCertForRenewal, setSelectedCertForRenewal] = useState(null);

  const [getSGBPApplicationList, { isLoading: sgbpApplicationListIsLoading, isSuccess: sgbpApplicationListIsSuccess, data: sgbpApplicationListData, },] = useLazyGetAllSGBPApplicationListByCorporateMemberIdForAdminQuery();
  const [getSGBSApplicationList, { isLoading: sgbsApplicationListIsLoading, isSuccess: sgbsApplicationListIsSuccess, data: sgbsApplicationListData, },] = useLazyGetAllSGBSApplicationListByCorporateMemberIdForAdminQuery();
  const [getCompanyInformation, { data: companyInformation, isLoading: companyInformationIsLoading, isSuccess: companyInformationIsSuccess },] = useLazyGetCompanyInformationByMemberIdQuery();
  const [getSGBSCertificateList, { data: sgbscCertificateListData, isLoading: sgbscCertificateListIsLoading, isSuccess: sgbscCertificateListIsSuccess, },] = useLazyGetAllSGBSCertificateListByCorporateMemberIdForAdminQuery();
  const [getSGBPCertificateList, { data: sgbpcCertificateListData, isLoading: sgbpcCertificateListIsLoading, isSuccess: sgbpcCertificateListIsSuccess, },] = useLazyGetAllSGBPCertificateListByCorporateMemberIdForAdminQuery();
  const [downloadIssuedCert, { isSuccess: isDownloadSuccess, },] = useDownloadIssuedCertificateMutation();

  const handleOpenRenewalModal = (cert) => {
    setSelectedCertForRenewal(cert);
    setShowRenewalModal(true);
  };

  const handleCloseRenewalModal = () => {
    setShowRenewalModal(false);
    setSelectedCertForRenewal(null);
  }; const [downloadMUAPdf] = useDownloadMUAPDFMutation();
  const [getMUAPathsByApplicationId, { isLoading: muaPathsLoading }] = useGetMUAPathsByApplicationIdMutation();

  useEffect(() => {
    if (editID > 0) {
      getCompanyInformation(editID);
      getSGBPApplicationList(editID);
      getSGBPCertificateList(editID);
      getSGBSApplicationList(editID);
      getSGBSCertificateList(editID);
    }
  }, [editID]);

  useEffect(() => {
    if (!companyInformationIsLoading && companyInformationIsSuccess && companyInformation) {
      setFormData((prev) => ({
        ...prev,
        initialState: {
          CompanyName: companyInformation?.companyName,
          CompanyUEN: companyInformation?.companyUEN,
          TypeOfCompany: companyInformation?.typeOfCompany,
        },
      }));
    }
  }, [companyInformationIsLoading, companyInformationIsSuccess, companyInformation]);

  const handleNavigate = (app, isProduct) => {
    if (isProduct) {
      navigate(`/productsApplication/${encryptAESID(app?.sgbpProductInformationId)}/${id}/${encryptAESID(app?.templateId)}`);
    } else {
      navigate(`/serviceApplication/${encryptAESID(app?.sgbpProductInformationId)}/${encryptAESID(editID)}/${encryptAESID(app?.templateId)}`);
    }
  }

  const sgbpApplications = useLoadMore(sgbpApplicationListData);
  const sgbsApplications = useLoadMore(sgbsApplicationListData);
  const sgbpCertificates = useLoadMore(sgbpcCertificateListData);
  const sgbsCertificates = useLoadMore(sgbscCertificateListData);

  if (sgbpApplicationListIsLoading || sgbsApplicationListIsLoading || companyInformationIsLoading || sgbscCertificateListIsLoading || sgbpcCertificateListIsLoading) {
    return <DataLoader />
  }

  const handleDownloadMUA = async (applicationId, subscriptionId, certificateNumber) => {
    const startTime = performance.now();
    try {
      setDownloadingMUAId(certificateNumber);

      // Step 1: Get the MUA file path
      const pathStart = performance.now();

      const muaFile = await getMUAPathsByApplicationId({
        SGBPProductInformationId: applicationId,
        CertificateNumber: certificateNumber,
      }).unwrap();

      console.log(
        "MUA path API:",
        (performance.now() - pathStart).toFixed(2),
        "ms"
      );

      const filePath = muaFile?.find(x => x.muaFilePath)?.muaFilePath;
      if (!filePath) {
        ToastService.error(
          muaFile?.length ? "MUA file path not found." : "MUA file not found."
        );
        return;
      }

      const downloadStart = performance.now();

      const blob = await downloadMUAPdf({
        Path: filePath,
      }).unwrap();
      if (!(blob instanceof Blob) || blob.size === 0) {
        ToastService.error("Downloaded file is empty or invalid.");
        return;
      }
      if (blob.type.includes("json") || blob.type.startsWith("text/")) {
        ToastService.error((await blob.text()) || "Unable to download MUA.");
        return;
      }
      // Step 3: Trigger the browser download
      const fileExt = filePath.match(/\.[^.]+$/)?.[0] || "";

      const fileName = filePath.replace(/^ProductServiceCertificates[\\/]/i, "").split(/[\\/]/).pop() || `certificate${fileExt}`;
      // saveAs(blob, fileName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Error downloading MUA:", error);

      ToastService.error(
        error?.data || error?.message || "Unable to download MUA."
      );
    } finally {
      setDownloadingMUAId(null);

      console.log(
        "Total MUA download time:",
        (performance.now() - startTime).toFixed(2),
        "ms"
      );
    }
  };

  const handleDownloadCert = async (path) => {
    try {
      const filePath = path;
      const fileExt = filePath?.match(/\.[^.]+$/)?.[0];
      const req = {
        Path: filePath,
      };

      const blob = await downloadIssuedCert(req).unwrap();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      link.download =
        filePath
          ?.replace(/^UploadResourceDocument[\\/]/i, "")
          ?.split(/[\\/]/)
          .pop() || `resource${fileExt || ""}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      error("File not found");
    }
  };




  return (
    <div className="view-certificate-account-page view-certificate-corporate-account-container">
      <div className="form-card">
        <h4 className="form-card-title">Company Information</h4>
        <div className="row status-input">
          <FormCreator ref={MemberApplicationRef} {...formData} />
        </div>
      </div>

      <div className="product-applications-card">
        <h3 className="section-heading table-card-heading">SGBC Product Applications</h3>
        <div className="table-responsive-wrapper">
          <table className="sgbc-applications-table">
            <thead>
              <tr>
                <th className="th-category">Category</th>
                <th className="th-product-type">Product Type</th>
                <th className="th-details">Product Details</th>
                <th className="th-status">Status</th>
                <th className="th-date">Submission Date</th>
                <th className="th-action"></th>

              </tr>
            </thead>
            <tbody>
              {sgbpApplications.total === 0 && !sgbpApplicationListIsLoading && (
                <tr>
                  <td colSpan={6} className="td-empty"><NoRecordFound /></td>
                </tr>
              )}
              {sgbpApplications.visibleData.map((app) => (
                <tr key={app.sgbpProductInformationId} className="table-data-row">
                  <td className="td-category">{app.category}</td>
                  <td className="td-product-type">{app.productType || "-"}</td>
                  <td className="td-details">{app.productDetails || "-"}</td>
                  <td className="td-status">
                    <span className={statusBadgeClass(app.status)}>{app.status}</span>
                  </td>
                  <td className="td-date">{formatDate(app.submissionDate)}</td>
                  <td className="td-action">
                    <button type="button" className="btn-view-action" onClick={() => handleNavigate(app, true)}>View</button>
                  </td >
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <LoadMoreButton
          canToggle={sgbpApplications.canToggle}
          isExpanded={sgbpApplications.isExpanded}
          onClick={sgbpApplications.toggle}
        />
      </div>

      <div className="product-applications-card">
        <h3 className="section-heading table-card-heading">SGBC Product Certificates</h3>
        <div className="table-responsive-wrapper">
          <table className="sgbc-applications-table">
            <thead>
              <tr>
                <th className="th-cert-no" style={{ width: "12%" }}>Cert No.</th>
                <th className="th-category" style={{ width: "25%" }}>Category</th>
                <th className="th-rating" style={{ width: "18%" }}>Rating</th>
                <th className="th-details" style={{ width: "28%" }}>Details</th>
                <th className="th-issued" style={{ width: "10%" }}>Issued</th>
                <th className="th-expiry" style={{ width: "10%" }}>Date of Expiry</th>
                <th className="th-action" style={{ width: "10%", minWidth: "120px" }}></th>
                <th className="th-action"></th>
                <th className="th-action"></th>
              </tr>
            </thead>
            <tbody>
              {sgbpCertificates.total === 0 && !sgbpcCertificateListIsLoading && (
                <tr>
                  <td colSpan={7} className="td-empty"><NoRecordFound /></td>
                </tr>
              )}
              {sgbpCertificates.visibleData.map((cert) => (
                <tr key={cert.sgbpProductInformationId} className="table-data-row">
                  <td className="td-cert-no font-weight-bold" onClick={() => handleDownloadCert(cert?.certificatePath)}> <p style={{ color: "#329a03", cursor: "pointer" }}>{cert.certificateNumber}</p></td>
                  <td className="td-category">{cert.category}</td>
                  <td className="td-rating">{cert.tickRating || "-"}</td>
                  <td className="td-details">{cert.productDetails || "-"}</td>
                  <td className="td-issued">{formatDate(cert.issuanceStartDate)}</td>
                  <td className="td-expiry">{formatDate(cert.issuanceEndDate)}</td>
                  <th className="th-action"></th>

                  <td className="td-action">
                    <div className="action-buttons-wrapper">
                      <button type="button" className="btn-view-action" onClick={() => handleNavigate(cert, true)}>View</button>
                      <button type="button" className="btn-update-action" onClick={() => handleOpenRenewalModal(cert)}>Update</button>
                    </div>
                  </td>
                  {cert?.isMuaSubmitted && (
                    <td className="td-action">
                      <button
                        type="button"
                        className="btn-view-action download-mua-btn"
                        // disabled={downloadingMUAId === cert?.sgbpsCertificateSubscriptionId}
                        disabled={downloadingMUAId === cert?.certificateNumber}
                        onClick={() => handleDownloadMUA(cert?.sgbpProductInformationId, cert?.sgbpsCertificateSubscriptionId, cert?.certificateNumber)}>
                        {
                          // downloadingMUAId === cert?.sgbpsCertificateSubscriptionId ?
                          downloadingMUAId === cert?.certificateNumber ?
                            (
                              <>
                                <span className="mua-download-loader"></span>
                                Downloading...
                              </>)
                            : ("Download MUA")}
                      </button>
                    </td >
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <LoadMoreButton
          canToggle={sgbpCertificates.canToggle}
          isExpanded={sgbpCertificates.isExpanded}
          onClick={sgbpCertificates.toggle}
        />
      </div>

      <div className="product-applications-card">
        <h3 className="section-heading table-card-heading">SGBC Services Applications</h3>
        <div className="table-responsive-wrapper">
          <table className="sgbc-applications-table">
            <thead>
              <tr>
                <th className="th-category" style={{ width: "25%" }}>Category</th>
                <th className="th-description" style={{ width: "45%" }}>Description</th>
                <th className="th-status" style={{ width: "15%" }}>Status</th>
                <th className="th-date" style={{ width: "12%" }}>Submission Date</th>
                <th className="th-action" style={{ width: "3%" }}></th>
                <th className="th-action"></th>
                <th className="th-action"></th>
              </tr>
            </thead>
            <tbody>
              {sgbsApplications.total === 0 && !sgbsApplicationListIsLoading && (
                <tr>
                  <td colSpan={5} className="td-empty"><NoRecordFound /></td>
                </tr>
              )}
              {sgbsApplications.visibleData.map((app) => (
                <tr key={app.sgbpProductInformationId} className="table-data-row">
                  <td className="td-category">{app.category || "-"}</td>
                  <td className="td-description">{app.description || "-"}</td>
                  <td className="td-status">
                    <span className={statusBadgeClass(app.status)}>{app.status}</span>
                  </td>
                  <td className="td-date">{formatDate(app.submissionDate)}</td>
                  <td className="td-action">
                    <button type="button" className="btn-view-action" onClick={() => handleNavigate(app, false)}>View</button>
                  </td>
                  {/* {app?.isMuaSubmitted && (
                    <td className="td-action">
                      <button type="button" className="btn-view-action download-mua-btn" disabled={downloadingMUAId === app?.sgbpProductInformationId} onClick={() => handleDownloadMUA(app?.sgbpProductInformationId)}>
                        {downloadingMUAId === app?.sgbpProductInformationId ? (
                          <>
                            <span className="mua-download-loader"></span>
                            Downloading...
                          </>)
                          : ("Download MUA")}
                      </button>
                    </td >
                  )} */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <LoadMoreButton
          canToggle={sgbsApplications.canToggle}
          isExpanded={sgbsApplications.isExpanded}
          onClick={sgbsApplications.toggle}
        />
      </div>
      <div className="product-applications-card">
        <h3 className="section-heading table-card-heading">SGBC Services Certificates</h3>

        <div className="table-responsive-wrapper">
          <table className="sgbc-applications-table">
            <thead>
              <tr>
                <th className="th-cert-no" style={{ width: "15%" }}>Cert No.</th>
                <th className="th-description" style={{ width: "35%" }}>Description</th>
                <th className="th-rating" style={{ width: "12%" }}>Rating</th>
                <th className="th-validity" style={{ width: "12%" }}>Validity</th>
                <th className="th-issued" style={{ width: "11%" }}>Issued</th>
                <th className="th-valid-till" style={{ width: "12%" }}>Valid Till</th>
                <th className="th-action" style={{ width: "3%" }}></th>
              </tr>
            </thead>
            <tbody>
              {sgbsCertificates.total === 0 && !sgbscCertificateListIsLoading && (
                <tr>
                  <td colSpan={7} className="td-empty"><NoRecordFound /></td>
                </tr>
              )}
              {sgbsCertificates.visibleData.map((cert) => {
                const isValid = cert.issuanceEndDate ? moment(cert.issuanceEndDate).isAfter(moment()) : true;
                return (
                  <tr key={cert.sgbpProductInformationId} className="table-data-row">
                    <td className="td-cert-no font-weight-bold" onClick={() => handleDownloadCert(cert?.certificatePath)}> <p style={{ color: "#329a03", cursor: "pointer" }}>{cert.certificateNumber}</p></td>
                    <td className="td-description">{cert.description || "-"}</td>
                    <td className="td-rating">{cert.tickRating || "-"}</td>
                    <td className="td-validity">{isValid ? "Valid" : "Expired"}</td>
                    <td className="td-issued">{formatDate(cert.issuanceStartDate)}</td>
                    <td className="td-valid-till">{formatDate(cert.issuanceEndDate)}</td>
                    <td className="td-action">
                      <button type="button" className="btn-view-action" onClick={() => handleNavigate(cert, false)}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <LoadMoreButton
          canToggle={sgbsCertificates.canToggle}
          isExpanded={sgbsCertificates.isExpanded}
          onClick={sgbsCertificates.toggle}
        />

      </div>
      <div className="d-flex justify-content-between mt-4">
        <Buttons
          buttonText="Back"
          buttonTypeClassName="black-btn"
          onClick={() => navigate("/certificateCorporateListPage")}
        />
      </div>
      {showRenewalModal && (
        <CertificationRenewalModal
          show={showRenewalModal}
          onClose={handleCloseRenewalModal}
          certificate={selectedCertForRenewal}
          companyName={companyInformation?.companyName}
        />
      )}
    </div>
  );
};

export default ViewCertificateCorporateAccount;

